import styled, { css } from 'styled-components';

import { floorGameDuration as duration, floorGameEasing as easing, styledReset } from '@pkg/primitives';

const buttonGroupGap = 10;

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    min-height: 100%;
    width: 100%;
    gap: 50px;
    padding: 0 0 25px 0;
`;

export const Section = styled.section`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 40px;
`;

export const PrimaryCopySection = styled(Section)`
    gap: 15px;
`;

export const EmojiImage = styled.img`
    display: block;
    width: 80px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        margin-top: 60px;
        width: 140px;
    }
`;

export const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
`;

export const FormRow = styled.div`
    display: flex;
    gap: 15px;
    justify-content: center;
`;

export const ErrorMessage = styled(FormRow)`
    color: #ff004c;
`;

export const ContentLegend = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
    width: 100%;
    margin: 10px 0;

    &::before,
    &::after {
        content: '';
        background: hsla(0, 0%, 100%, 0.15);
        flex-grow: 1;
        display: block;
        height: 2px;
    }
`;

export const EmphasizedTypography = styled.em`
    color: white;
    font-style: normal;
`;

export const ButtonList = styled.ul`
    ${styledReset.list};
    display: flex;
    flex-wrap: wrap;
    gap: ${buttonGroupGap}px;
`;

export const ButtonItem = styled.li`
    flex: 1 1 auto;

    // Art direction to favour a heavier first row (fewer items).
    &:nth-child(-n + 2) {
        min-width: calc(50% - ${buttonGroupGap}px);
    }

    .yat-button {
        width: 100%;

        @media (max-width: ${({ theme }) => theme.breakpoints.mobileSmall}) {
            padding: 0 20px;
            min-height: 45px;
        }
    }
`;

export const TwitterLink = styled.a`
    color: hsla(204, 88%, 53%, 1);
    transition: color 250ms;
`;

export const AuthPanelSteps = styled.div`
    height: 100%;
    position: relative;
`;

type ButtonIconProps = {
    size?: 'default' | 'font-size';
};

const BUTTON_ICON_SIZES = {
    default: '1.25em',
    'font-size': '1em',
};

export const ButtonIcon = styled.div<ButtonIconProps>`
    height: ${(props) => BUTTON_ICON_SIZES[props.size ?? 'default']};
    width: ${(props) => BUTTON_ICON_SIZES[props.size ?? 'default']};
    margin: 0 0.5em 0 0;
    display: flex;
    justify-content: stretch;
`;

export const AuthFooterContainer = styled.footer`
    padding-bottom: 30px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    text-align: center;
`;

const cssLinkBase = css`
    ${styledReset.link};
    color: rgb(255, 255, 255, 0.9);
    text-decoration: underline 1px transparent;
    transition-property: color, text-decoration-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &:hover {
        color: rgb(255, 255, 255, 1);
        // TODO: Simplify this once we are no longer competing with
        // problematic global reset styling.
        // text-decoration-color: currentColor;
        text-decoration: underline 1px currentColor !important;
    }

    &:active {
        color: rgb(255, 255, 255, 0.9);
    }
`;

export const InternalLink = styled.a`
    ${cssLinkBase};
`;

export const ExternalLink = styled.a.attrs({ target: '_blank', rel: 'noopener noreferrer' })`
    ${cssLinkBase};
`;
